import React, {
  ReactNode,
  useEffect,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { InviteContext } from '.';
import { useInvites } from '../../hooks';
import { ActiveChannelTypeEnum } from 'constants-channel';
import { useInviteFromEmail } from '../../hooks/useInviteFromEmail';

type Props = {
  children: ReactNode;
  email?: string;
};

export const InviteContextProvider: React.FC<Props> = ({ children }) => {
  const [inviteId, setInviteId] = useState<string | undefined>(undefined);
  const [email, setEmail] = useState<string | undefined>(undefined);

  const { inviteId: inviteIdFromEmail, loading: inviteIdFromEmailLoading } =
    useInviteFromEmail(email);

  useEffect(() => {
    if (!inviteId && email && inviteIdFromEmail) {
      setInviteId(inviteIdFromEmail);
    }
  }, [inviteId, email, inviteIdFromEmail]);

  const {
    invite: inviteInfo,
    loading,
    error,
    claimInvite: hookClaimInvite,
    claiming,
    claimError,
  } = useInvites({
    inviteId,
  });

  const setInviteIdCallback = useCallback(
    (inviteId: string) => {
      setInviteId(inviteId);
    },
    [inviteId]
  );
  const setEmailCallback = useCallback(
    (email: string) => {
      // Only want to set email if we don't have an inviteId, so we can use it to try tofetch one
      if (!inviteId) {
        setEmail(email);
      }
    },
    [inviteId, email]
  );

  const clearInvite = useCallback(() => {
    setInviteId(undefined);
    setEmail(undefined);
  }, [inviteId]);

  const claimInvite = useCallback(() => {
    if (!inviteInfo?.channel?._id || !inviteId) {
      return;
    }

    return hookClaimInvite(inviteId, inviteInfo?.channel?._id);
  }, [hookClaimInvite, inviteId, inviteInfo?.channel._id]);

  const contextValue = useMemo(() => {
    const isInvitedToProperty =
      inviteInfo?.channel?.type === ActiveChannelTypeEnum.Property;

    const building = isInvitedToProperty ? inviteInfo?.channel : undefined;
    const company = isInvitedToProperty
      ? inviteInfo?.company
      : inviteInfo?.channel;

    return {
      inviteId,
      email: inviteInfo?.email,
      userName: inviteInfo?.name,
      company,
      building,
      loading: loading || inviteIdFromEmailLoading,
      setInviteId: setInviteIdCallback,
      setEmail: setEmailCallback,
      claimInvite,
      clearInvite,
      claiming,
      claimError,
      error,
    };
  }, [
    email,
    inviteIdFromEmail,
    inviteInfo,
    inviteId,
    error,
    claimError,
    claiming,
    loading,
    inviteIdFromEmailLoading,
  ]);

  return (
    <InviteContext.Provider value={contextValue}>
      {children}
    </InviteContext.Provider>
  );
};
